import { useCallback, useMemo } from "react";
import {
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuSubTrigger,
  MenuSubPopup,
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
  Icon,
  Text,
  toastManager,
} from "@salestrak/ui";

import { useAppData, type MemberRow, type RoleRow } from "#app/lib/data";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useSoleManagerTeams } from "#app/lib/hooks/use-sole-manager-teams";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MemberRowMenuProps = {
  member: MemberRow;
  availableRoles: RoleRow[];
  onViewProfile: (userId: string) => void;
  onRoleChange: (userId: string, roleId: string) => void;
};

export type ConfirmAction = {
  type: "suspend" | "reactivate" | "cancel-invite";
  member: MemberRow;
};

// The viewer's own row offers no Suspend: the API refuses a member changing
// their own status, and leaving is the profile's Leave workspace.
function useIsCurrentUser(userId: string): boolean {
  const { currentUser } = useAppData();
  return currentUser?.userId === userId;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Menu items for member row actions. Returns only MenuItem elements.
 * Dialogs are rendered separately via MemberRowMenuDialog.
 */
export function MemberRowMenuItems({
  member,
  availableRoles,
  onViewProfile,
  onRoleChange,
  onRequestSuspend,
  onRequestReactivate,
  onRequestCancelInvite,
  onRequestReinvite,
}: MemberRowMenuProps & {
  onRequestSuspend: (member: MemberRow) => void;
  onRequestReactivate: (member: MemberRow) => void;
  onRequestCancelInvite: (member: MemberRow) => void;
  /** A member who left comes back through a fresh invite they accept —
   *  never an admin flipping them active. Opens the invite dialog seeded
   *  with their address. */
  onRequestReinvite: (member: MemberRow) => void;
}) {
  const { resendInvite } = useMemberMutations();
  const isSelf = useIsCurrentUser(member.userId);

  const canUpdateMembers = usePermission("manage.users");
  const canUpdateRoles = usePermission("manage.users");
  const canResendInvite = usePermission("manage.users");

  const { memberStatus } = member;

  const memberRoleIds = useMemo(
    () => new Set(member.roles.map((r) => r.roleId)),
    [member.roles]
  );

  const handleResendInvite = useCallback(async () => {
    await resendInvite(member.userId);
  }, [member.userId, resendInvite]);

  if (!canUpdateMembers && !canUpdateRoles && !canResendInvite) {
    return null;
  }

  return (
    <>
      <MenuItem onClick={() => onViewProfile(member.userId)}>
        <Icon name="user" size="md" color="muted" />
        <Text size="sm">View profile</Text>
      </MenuItem>

      {canUpdateRoles && availableRoles.length > 0 ? (
        <>
          <MenuSeparator />
          <MenuSub>
            <MenuSubTrigger>
              <Icon name="shield" size="md" color="muted" />
              <Text size="sm">Change role</Text>
            </MenuSubTrigger>
            <MenuSubPopup>
              {availableRoles.map((role) => {
                const isSelected = memberRoleIds.has(role.roleId);
                return (
                  <MenuItem
                    key={role.roleId}
                    onClick={() => onRoleChange(member.userId, role.roleId)}
                  >
                    <Text size="sm">{role.roleName}</Text>
                    {isSelected ? (
                      <Icon name="check" size="md" color="primary" />
                    ) : null}
                  </MenuItem>
                );
              })}
            </MenuSubPopup>
          </MenuSub>
        </>
      ) : null}

      {canUpdateMembers && memberStatus === "active" && !isSelf ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={() => onRequestSuspend(member)}>
            <Icon name="x-circle" size="md" color="muted" />
            <Text size="sm">Suspend</Text>
          </MenuItem>
        </>
      ) : null}

      {canUpdateMembers && memberStatus === "suspended" ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={() => onRequestReactivate(member)}>
            <Icon name="play-circle" size="md" color="muted" />
            <Text size="sm">Reactivate member</Text>
          </MenuItem>
        </>
      ) : null}

      {canUpdateMembers && memberStatus === "left" && member.email ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={() => onRequestReinvite(member)}>
            <Icon name="send" size="md" color="muted" />
            <Text size="sm">Re-invite</Text>
          </MenuItem>
        </>
      ) : null}

      {memberStatus === "invited" && canResendInvite ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={handleResendInvite}>
            <Icon name="send" size="md" color="muted" />
            <Text size="sm">Resend invite</Text>
          </MenuItem>
          <MenuItem onClick={() => onRequestCancelInvite(member)}>
            <Icon name="x-circle" size="md" color="muted" />
            <Text size="sm">Cancel invite</Text>
          </MenuItem>
        </>
      ) : null}
    </>
  );
}

/**
 * Menu items for member rows on the app-level members view — a lighter menu
 * than the settings roster's: copy the member's id, plus suspend for viewers
 * with manage.users. Pair with MemberRowMenuDialog for the confirmation.
 */
export function AppMemberRowMenuItems({
  member,
  onRequestSuspend,
}: {
  member: MemberRow;
  onRequestSuspend: (member: MemberRow) => void;
}) {
  const canUpdateMembers = usePermission("manage.users");
  const isSelf = useIsCurrentUser(member.userId);

  const handleCopyId = useCallback(() => {
    void copyToClipboard(member.userId).then((ok) => {
      toastManager.add(
        ok
          ? { title: "Member ID copied", type: "success" }
          : { title: "Failed to copy member ID", type: "error" },
      );
    });
  }, [member.userId]);

  return (
    <>
      <MenuItem onClick={handleCopyId}>
        <Icon name="copy" size="md" color="muted" />
        <Text size="sm">Copy member ID</Text>
      </MenuItem>

      {canUpdateMembers && member.memberStatus === "active" && !isSelf ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={() => onRequestSuspend(member)}>
            <Icon name="x-circle" size="md" color="muted" />
            <Text size="sm">Suspend</Text>
          </MenuItem>
        </>
      ) : null}
    </>
  );
}

/**
 * Confirmation dialog for member suspend/reactivate.
 * Render as a sibling to DataTable, not inside renderRowActions.
 */
export function MemberRowMenuDialog({
  action,
  onClose,
  onConfirmed,
}: {
  action: ConfirmAction | null;
  onClose: () => void;
  /** The action went through on the server (a rejected one only toasts).
   *  The profile island closes itself here after a cancelled invite — the
   *  member it presented no longer exists. */
  onConfirmed?: (action: ConfirmAction) => void;
}) {
  const { updateMember, cancelInvite } = useMemberMutations();

  const memberLabel = action?.member.name ?? action?.member.email ?? "";

  const handleConfirm = useCallback(async () => {
    if (!action) return;
    const result =
      action.type === "cancel-invite"
        ? await cancelInvite(action.member.userId)
        : await updateMember(action.member.userId, {
            memberStatus: action.type === "suspend" ? "suspended" : "active",
          });
    if (result) {
      // Suspend/reactivate flip a status badge that is easy to miss; a
      // cancelled invite drops the row, which is its own feedback.
      if (action.type !== "cancel-invite") {
        toastManager.add({
          title: action.type === "suspend" ? "Member suspended" : "Member reactivated",
          type: "success",
        });
      }
      onConfirmed?.(action);
    }
    onClose();
  }, [action, updateMember, cancelInvite, onClose, onConfirmed]);

  const title =
    action?.type === "suspend"
      ? `Suspend ${memberLabel}?`
      : action?.type === "reactivate"
        ? `Reactivate ${memberLabel}?`
        : `Cancel invite for ${memberLabel}?`;

  // Suspending a team's only manager leaves that team with nobody to run it;
  // the dialog names those teams so the admin can hand them over first.
  const soleManagerTeams = useSoleManagerTeams(
    action?.type === "suspend" ? action.member.userId : null,
  );
  const soleManagerNote =
    soleManagerTeams.length > 0
      ? ` They are the only manager of ${soleManagerTeams.join(", ")}, which will have no active manager.`
      : "";

  const description =
    action?.type === "suspend"
      ? `${memberLabel} will no longer be able to access this workspace.${soleManagerNote}`
      : action?.type === "reactivate"
        ? `${memberLabel} will regain access to this workspace.`
        : `${memberLabel} will be removed from this workspace. The invitation link in their email will stop working.`;

  return (
    <AlertDialog
      open={!!action}
      onOpenChange={(open) => { if (!open) onClose(); }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          {action?.type === "reactivate" ? (
            <Button onClick={handleConfirm}>Reactivate member</Button>
          ) : (
            <Button color="destructive" onClick={handleConfirm}>
              {action?.type === "suspend" ? "Suspend" : "Cancel invite"}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
